import React, { useState, useEffect } from 'react';
import { textFieldStyles } from './fieldStyles';
 
export interface TimeFieldProps {
  label?: string;
  value?: string; // Formato "HH:MM"
  onChange: (time: string) => void;
  minTime?: string; // Formato "HH:MM"
  maxTime?: string; // Formato "HH:MM"
  allowedMinutes?: number[]; // Array de minutos permitidos, ej: [0, 15, 30, 45]
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}
 
export const TimeField: React.FC<TimeFieldProps> = ({
  label,
  value = '',
  onChange,
  minTime = '00:00',
  maxTime = '23:59',
  allowedMinutes = [0, 15, 30, 45], // Por defecto: cada 15 minutos
  placeholder = 'HH:MM',
  error,
  disabled = false,
  required = false,
  className = ''
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [filteredTimes, setFilteredTimes] = useState<string[]>([]);
 
  // Generar todas las combinaciones de hora:minuto válidas
  const generateValidTimes = (): string[] => {
	const times: string[] = [];
	 
	for (let hour = 0; hour < 24; hour++) {
	  for (const minute of allowedMinutes) {
		const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
		 
		// Verificar si está dentro del rango permitido
		if (isTimeInRange(timeString, minTime, maxTime)) {
		  times.push(timeString);
		}
	  }
	}
	 
	return times;
  };
 
  // Verificar si un tiempo está dentro del rango
  const isTimeInRange = (time: string, min: string, max: string): boolean => {
	const timeMinutes = timeToMinutes(time);
	const minMinutes = timeToMinutes(min);
	const maxMinutes = timeToMinutes(max);
	 
	return timeMinutes >= minMinutes && timeMinutes <= maxMinutes;
  };
 
  // Convertir tiempo a minutos desde medianoche
  const timeToMinutes = (time: string): number => {
	const [hours, minutes] = time.split(':').map(Number);
	return hours * 60 + minutes;
  };
 
  // Convertir minutos a tiempo
  const minutesToTime = (minutes: number): string => {
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };
 
  // Formatear input mientras el usuario escribe
  const formatInput = (input: string): string => {
	// Remover caracteres no numéricos excepto ':'
	let cleaned = input.replace(/[^\d:]/g, '');
	 
	// Limitar a 5 caracteres máximo (HH:MM)
	if (cleaned.length > 5) {
	  cleaned = cleaned.substring(0, 5);
	}
	 
	// Auto-insertar ':' después de 2 dígitos
	if (cleaned.length === 2 && !cleaned.includes(':')) {
	  cleaned += ':';
	}
	 
	return cleaned;
  };
 
  // Validar formato de tiempo
  const isValidTimeFormat = (time: string): boolean => {
	const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
	return timeRegex.test(time);
  };
 
  // Filtrar tiempos basado en el input
  const filterTimes = (input: string) => {
	if (!input || input.length < 1) {
	  setFilteredTimes(generateValidTimes());
	  return;
	}
 
	const allTimes = generateValidTimes();
	const filtered = allTimes.filter(time => 
	  time.toLowerCase().includes(input.toLowerCase())
	);
	 
	setFilteredTimes(filtered);
  };
 
  // Manejar cambio de input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
	const formatted = formatInput(e.target.value);
	setInputValue(formatted);
	filterTimes(formatted);
	setIsOpen(true);
  };
 
  // Manejar selección de tiempo
  const handleTimeSelect = (selectedTime: string) => {
	setInputValue(selectedTime);
	setIsOpen(false);
	onChange(selectedTime);
  };
 
  // Manejar blur del input
  const handleBlur = () => {
	// Delay para permitir click en dropdown
	setTimeout(() => {
	  setIsOpen(false);
	   
	  // Validar y corregir el valor si es necesario
	  if (inputValue && isValidTimeFormat(inputValue)) {
		const timeMinutes = timeToMinutes(inputValue);
		 
		// Encontrar el tiempo más cercano permitido
		const allTimes = generateValidTimes();
		const closestTime = allTimes.reduce((closest, time) => {
		  const timeMinutesDiff = Math.abs(timeToMinutes(time) - timeMinutes);
		  const closestMinutesDiff = Math.abs(timeToMinutes(closest) - timeMinutes);
		  return timeMinutesDiff < closestMinutesDiff ? time : closest;
		});
		 
		if (closestTime !== inputValue) {
		  setInputValue(closestTime);
		  onChange(closestTime);
		} else {
		  onChange(inputValue);
		}
	  } else if (inputValue) {
		// Si el formato no es válido, limpiar
		setInputValue('');
		onChange('');
	  }
	}, 150);
  };
 
  // Manejar focus del input
  const handleFocus = () => {
	setIsOpen(true);
	filterTimes(inputValue);
  };
 
  // Sincronizar con prop value
  useEffect(() => {
	setInputValue(value);
  }, [value]);
 
  // Generar opciones de minutos para el dropdown
  const generateMinuteOptions = (hour: number): string[] => {
	return allowedMinutes
	  .map(minute => `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`)
	  .filter(time => isTimeInRange(time, minTime, maxTime));
  };
 
  const containerStyles: React.CSSProperties = {
	position: 'relative',
	width: '100%',
	marginBottom: '16px'
  };
 
  const labelStyles: React.CSSProperties = {
	display: 'block',
	marginBottom: '8px',
	fontWeight: '600',
	color: '#2c3e50',
	fontSize: '14px'
  };
 
  const inputStyles: React.CSSProperties = {
	...textFieldStyles,
	width: '100px',
	paddingRight: '40px',
	backgroundColor: disabled ? '#f8f9fa' : 'white',
	cursor: disabled ? 'not-allowed' : 'text'
  };
 
  const dropdownStyles: React.CSSProperties = {
	position: 'absolute',
	top: '100%',
	left: 0,
	right: 0,
	backgroundColor: 'white',
	border: '1px solid #e9ecef',
	borderTop: 'none',
	borderRadius: '0 0 4px 4px',
	maxHeight: '200px',
	overflowY: 'auto',
	zIndex: 1000,
	boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  };
 
  const optionStyles: React.CSSProperties = {
	padding: '8px 12px',
	cursor: 'pointer',
	borderBottom: '1px solid #f8f9fa',
	fontSize: '14px',
	color: '#2c3e50'
  };
 
  const optionHoverStyles: React.CSSProperties = {
	backgroundColor: '#f8f9fa'
  };
 
  const errorStyles: React.CSSProperties = {
	color: '#dc3545',
	fontSize: '12px',
	marginTop: '4px'
  };
 
  const requiredStyles: React.CSSProperties = {
	color: '#dc3545'
  };
 
  return (
	<div className={className} style={containerStyles}>
	  {label && (
		<label style={labelStyles}>
		  {label}
		  {required && <span style={requiredStyles}> *</span>}
		</label>
	  )}
	   
	  <div style={{ position: 'relative' }}>
		<input
		  type="text"
		  value={inputValue}
		  onChange={handleInputChange}
		  onFocus={handleFocus}
		  onBlur={handleBlur}
		  placeholder={placeholder}
		  disabled={disabled}
		  style={inputStyles}
		  maxLength={5}
		/>
		 
		{isOpen && filteredTimes.length > 0 && (
		  <div style={dropdownStyles}>
			{filteredTimes.slice(0, 20).map((time, index) => (
			  <div
				key={time}
				style={optionStyles}
				onMouseDown={() => handleTimeSelect(time)}
				onMouseEnter={(e) => {
				  e.currentTarget.style.backgroundColor = '#f8f9fa';
				}}
				onMouseLeave={(e) => {
				  e.currentTarget.style.backgroundColor = 'white';
				}}
			  >
				{time}
			  </div>
			))}
			{filteredTimes.length > 20 && (
			  <div style={{ ...optionStyles, color: '#6c757d', fontStyle: 'italic' }}>
				... y {filteredTimes.length - 20} más
			  </div>
			)}
		  </div>
		)}
	  </div>
	   
	  {error && (
		<div style={errorStyles}>
		  {error}
		</div>
	  )}
	</div>
  );
};
